import React, {
  useCallback, useEffect, useState, useMemo, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import TableRowForm from '../../../components/table-row-form/table-row-form.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import Loader from '../../../components/loader/loader.component';
import ActionButton from '../../../components/action-button/action-button.component';
import IconBlockError from '../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../components/icon-block-success/icon-block-success.component';
import TextBlock from '../../../components/text-block/text-block.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldPhone from '../../../components/form-field-phone/form-field-phone.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FieldPhoneBlock from '../../../components/field-phone-block/field-phone-block.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useShiftState from '../../../hooks/use-shift-state';
import useScreenSmall from '../../../hooks/use-screen-small';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import { CONTACT_TYPE_COMPANY, CONTACT_TYPE_PERSON } from '../../../constants/entity/contact-types';
import URL_LINKEDIN_PROFILE_PREFIX from '../../../constants/system/urls';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const ContactRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isCardView,
    contactIndex,
    contactData,
    tagList,
    tableName,
    tableColumnSettings,
    inputRefs,
    fileDropDeps,
    currentUser,
    contactGroupList,
    contactTypeList,
    contactCompanyList,
    assetTypeList,
    onInlineSave,
    onInlineCancel,
    onTagsChange,
    onContactPhotoChange,
    onContactPhotoDelete,
    onAssetTypesChange,
    onPhoneChange,
    onPhoneWorkChange,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onContactGroupsChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const isShiftPressed: boolean = useShiftState();

  const [contactType, setContactType]: [string | null, Function] = useState(null);
  const [contactPhotoFile, setContactPhotoFile]: [File | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType | null, Function] = useState(null);
  const [selectedContactGroups, setSelectedContactGroups]: [ContactGroup | null, Function] = useState(null);

  const contactId: number = contactData.id;
  const formKeyContact: string = FormService.getIndexedFormFieldKey(contactId);
  const formPrefix: string = `contactListData[${formKeyContact}]`;
  const photoDefaultPreviewUrl: string = (contactData.photoThumbnail || contactData.photo)
    || (!contactData.photoThumbnail && !contactData.photo && contactData.type === CONTACT_TYPE_COMPANY
      ? CompanyDefaultImage : PersonDefaultImage);
  const withIsDefaultColumn: boolean = !!currentUser && currentUser.isPlatformAdmin;

  const menuItemContactTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactTypeList,
  }), [contactTypeList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: contactCompanyList,
    optionItemImageKey: 'photo',
    optionItemImageThumbnailKey: 'photoThumbnail',
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactGroupList,
    selectedItemList: selectedContactGroups,
  }), [contactGroupList, selectedContactGroups]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const contactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    if (onContactGroupsChange) {
      onContactGroupsChange(contactId, contactGroupValues);
    }

    setSelectedContactGroups(contactGroupValues);
  }, [contactId]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(contactId, assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, [contactId]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(contactId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [contactId]);

  const phoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (onPhoneChange) {
      onPhoneChange(contactId, phoneValue);
    }
  }, [contactId]);

  const phoneWorkChange: PhoneChangeHandlerFunction = useCallback((phoneWorkValue?: string): void => {
    if (onPhoneWorkChange) {
      onPhoneWorkChange(contactId, phoneWorkValue);
    }
  }, [contactId]);

  const contactPhotoChange: Function = useCallback((file: File): void => {
    setContactPhotoFile(file);

    if (onContactPhotoChange) {
      onContactPhotoChange(contactId, file);
    }
  }, [contactId]);

  const contactPhotoDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setContactPhotoFile(null);

    if (onContactPhotoDelete) {
      onContactPhotoDelete(contactId);
    }

    event.stopPropagation();
  }, [contactId]);

  const changeContactType: Function = useCallback((contactTypeValue: ContactType): void => {
    setContactType(contactTypeValue);
  }, []);

  const tableContactListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;

        return (
          <TableCellShort isTextCenter>
            <FormFieldUpload
              isImage
              isInline
              isDroppable
              isListImage
              isDisabled={isDisabled}
              name={`${formPrefix}.photo`}
              uploadFile={contactPhotoFile}
              buttonLabel={contactData.fullName}
              dropDeps={fileDropDeps}
              defaultPreviewUrl={contactData.photoThumbnail || contactData.photo
                || (contactData.type === CONTACT_TYPE_COMPANY ? CompanyDefaultImage : PersonDefaultImage)}
              acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
              inputRefs={inputRefs}
              onFileChange={contactPhotoChange}
              onFileDelete={contactPhotoDelete}
            />
          </TableCellShort>
        );
      },
    },
    {
      id: 2,
      name: 'fullName',
      component: (): ReactElement => (
        <TableCell isText isSticky isStickyLeft isDisabled>
          {contactData.fullName}
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;

        return (
          <TableCell isDisabled={contactType !== CONTACT_TYPE_COMPANY}>
            {
              contactType === CONTACT_TYPE_COMPANY ? (
                <FormFieldText
                  isDisabled={isDisabled}
                  name={`${formPrefix}.name`}
                  inputRefs={inputRefs}
                  inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
                />
              ) : '-'
            }
          </TableCell>
        );
      },
    },
    {
      id: 4,
      name: 'firstName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;

        return (
          <TableCell isDisabled={contactType !== CONTACT_TYPE_PERSON}>
            {
              contactType === CONTACT_TYPE_PERSON ? (
                <FormFieldText
                  isDisabled={isDisabled}
                  name={`${formPrefix}.firstName`}
                  inputRefs={inputRefs}
                  inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
                />
              ) : '-'
            }
          </TableCell>
        );
      },
    },
    {
      id: 5,
      name: 'lastName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;

        return (
          <TableCell isDisabled={contactType !== CONTACT_TYPE_PERSON}>
            {
              contactType === CONTACT_TYPE_PERSON ? (
                <FormFieldText
                  isDisabled={isDisabled}
                  name={`${formPrefix}.lastName`}
                  inputRefs={inputRefs}
                  inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
                />
              ) : '-'
            }
          </TableCell>
        );
      },
    },
    {
      id: 6,
      name: 'type.title',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.id || !!contactData.user
          || (contactTypeList && !contactTypeList.length);

        return (
          <TableCell>
            <FormFieldSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.type`}
              optionsTotal={contactTypeList && contactTypeList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
              onChange={((contactTypeName: string): void => {
                changeContactType(contactTypeName);
              })}
            >
              {menuItemContactTypeList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 7,
      name: 'company.name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactCompanyList && !contactCompanyList.length;

        return (
          <TableCell>
            {
              contactType === CONTACT_TYPE_PERSON && (
                <FormFieldSelect
                  withSearch
                  withBlankElement
                  isDisabled={isDisabled}
                  isReadOnly={isShiftPressed}
                  name={`${formPrefix}.companyId`}
                  optionsTotal={contactCompanyList && contactCompanyList.length}
                  inputRefs={inputRefs}
                  inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
                  onSearch={onContactCompanySearch}
                  onClose={onContactCompanySearchClose}
                >
                  {menuItemContactCompanyList}
                </FormFieldSelect>
              )
            }
          </TableCell>
        );
      },
    },
    {
      id: 8,
      name: 'group.title',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactGroupList && !contactGroupList.length;

        return (
          <TableCell>
            <FormFieldMultiSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.groupIds`}
              optionList={contactGroupList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
              onValuesChange={contactGroupsChange}
            >
              {menuItemContactGroupList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 9,
      name: 'phone',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;
        const subFieldIndexPhone: number = 1;
        const subFieldIndexPhoneExtension: number = 2;

        return (
          <TableCell>
            <FieldPhoneBlock>
              <FormFieldPhone
                isDisabled={isDisabled}
                name={`${formPrefix}.phone`}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}.${subFieldIndexPhone}` : null}
                onChange={phoneChange}
              />
              <FormFieldNumber
                withNoCommas
                isDisabled={isDisabled}
                name={`${formPrefix}.phoneExtension`}
                placeholder="Ext"
                inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}.${subFieldIndexPhoneExtension}` : null}
                onChange={phoneWorkChange}
              />
            </FieldPhoneBlock>
          </TableCell>
        );
      },
    },
    {
      id: 10,
      name: 'phoneWork',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;
        const subFieldIndexPhone: number = 1;
        const subFieldIndexPhoneExtension: number = 2;

        return (
          <TableCell>
            <FieldPhoneBlock>
              <FormFieldPhone
                isDisabled={isDisabled}
                name={`${formPrefix}.phoneWork`}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}.${subFieldIndexPhone}` : null}
                onChange={phoneWorkChange}
              />
              <FormFieldNumber
                withNoCommas
                isDisabled={isDisabled}
                name={`${formPrefix}.phoneWorkExtension`}
                placeholder="Ext"
                inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}.${subFieldIndexPhoneExtension}` : null}
              />
            </FieldPhoneBlock>
          </TableCell>
        );
      },
    },
    {
      id: 11,
      name: 'email',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !!contactData.user;

        return (
          <TableCell>
            <FormFieldText
              isDisabled={isDisabled}
              name={`${formPrefix}.email`}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
            />
          </TableCell>
        );
      },
    },
    {
      id: 12,
      name: 'streetAddress',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FieldAddressBlock isInline>
            <FormFieldAddress
              itemId={contactId}
              name={`${formPrefix}.streetAddress`}
              inputRefs={inputRefs}
              inputRefIndex={`${contactIndex}.${fieldIndex}`}
              onAddressSearchCompleted={(addressObject: JsonObject): void => {
                onAddressSearchCompleted(addressObject, contactId);
              }}
              onKeyDown={(): void => {
                onAddressFieldKeyDown(contactId);
              }}
            />
            <FormFieldText isDisabled name={`${formPrefix}.location`} />
            <FormFieldText isDisabled name={`${formPrefix}.postalCode`} />
            <FormFieldText isHidden name={`${formPrefix}.address`} />
          </FieldAddressBlock>
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'addressDetails',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isInline
            isMultiline
            name={`${formPrefix}.addressDetails`}
            maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
            minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
            inputRefs={inputRefs}
            inputRefIndex={`${contactIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'linkedin',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.linkedin`}
            placeholder={`${URL_LINKEDIN_PROFILE_PREFIX}...`}
            inputRefs={inputRefs}
            inputRefIndex={`${contactIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'title',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_PERSON && (
              <FormFieldText
                name={`${formPrefix}.title`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 16,
      name: 'description',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isInline
            isMultiline
            name={`${formPrefix}.description`}
            maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
            minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
            inputRefs={inputRefs}
            inputRefIndex={`${contactIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'industry',
      component: (tableField: TableField, fieldIndex: number): ReactElement | boolean => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <FormFieldText
                name={`${formPrefix}.industry`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 18,
      name: 'foundationYear',
      component: (tableField: TableField, fieldIndex: number): ReactElement | boolean => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <FormFieldNumber
                withNoCommas
                name={`${formPrefix}.foundationYear`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 19,
      name: 'headQuarter',
      component: (tableField: TableField, fieldIndex: number): ReactElement | boolean => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <FormFieldText
                name={`${formPrefix}.headQuarter`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 20,
      name: 'website',
      component: (tableField: TableField, fieldIndex: number): ReactElement | boolean => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <FormFieldText
                name={`${formPrefix}.website`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'keySubsidiaries',
      component: (tableField: TableField, fieldIndex: number): ReactElement | boolean => (
        <TableCell>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <FormFieldText
                name={`${formPrefix}.keySubsidiaries`}
                inputRefs={inputRefs}
                inputRefIndex={`${contactIndex}.${fieldIndex}`}
              />
            )
          }
        </TableCell>
      ),
    },
    {
      id: 22,
      name: 'tags',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = tagList && !tagList.length;

        return (
          <TableCell>
            <FormFieldMultiSelect
              isAbleToAdd
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.tags`}
              optionList={tagList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
              onSearch={onTagSearch}
              onClose={onTagSearchClose}
              onValuesChange={tagsChange}
            >
              {menuItemTagList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 23,
      name: 'assetTypes',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = assetTypeList && !assetTypeList.length;

        return (
          <TableCell>
            <FormFieldMultiSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.assetTypes`}
              optionList={assetTypeList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${contactIndex}.${fieldIndex}` : null}
              onValuesChange={assetTypesChange}
            >
              {menuItemAssetTypeList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 24,
      name: 'updates',
      component: (): ReactElement => (
        <Fragment />
      ),
    },
    {
      id: 25,
      name: 'relatedContacts',
      component: (): ReactElement => (
        <Fragment />
      ),
    },
    {
      id: 26,
      name: 'actions',
      isNotFiltered: true,
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(contactData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(contactData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], [
    isShiftPressed, contactPhotoFile, isSaving, isSaveDisabled, withIsDefaultColumn, selectedTags, selectedAssetTypes,
    selectedContactGroups, fileDropDeps, contactData, contactCompanyList, tagList,
  ]);

  useEffect((): void => {
    if (contactData) {
      if (!contactType) {
        setContactType(contactData.type || null);
      }

      if (!selectedTags) {
        setSelectedTags(contactData.tags || []);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(contactData.assetTypes || []);
      }

      if (!selectedContactGroups) {
        setSelectedContactGroups(contactData.groups || []);
      }
    }
  }, [contactData]);

  return (isCardView || isSmallScreen) ? (
    <Card isContainer>
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Photo
            </TableCell>
            <TableCell>
              <FormFieldUpload
                isImage
                isDroppable
                isListImage
                isDisabled={!!contactData.user}
                name={`${formPrefix}.photo`}
                uploadFile={contactPhotoFile}
                buttonLabel={contactData.fullName}
                dropDeps={fileDropDeps}
                defaultPreviewUrl={photoDefaultPreviewUrl}
                acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                onFileChange={contactPhotoChange}
                onFileDelete={contactPhotoDelete}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Type
            </TableCell>
            <TableCell>
              <FormFieldSelect
                isBordered
                isDisabled={!!contactData.id || !!contactData.user || (contactTypeList && !contactTypeList.length)}
                name={`${formPrefix}.type`}
                optionsTotal={contactTypeList && contactTypeList.length}
                onChange={((contactTypeName: string): void => {
                  changeContactType(contactTypeName);
                })}
              >
                {menuItemContactTypeList}
              </FormFieldSelect>
            </TableCell>
          </TableRow>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Name
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered isDisabled={!!contactData.user} name={`${formPrefix}.name`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_PERSON && (
              <TableRow>
                <TableCell>
                  <TextBlock>
                    First Name
                  </TextBlock>
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered isDisabled={!!contactData.user} name={`${formPrefix}.firstName`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_PERSON && (
              <TableRow>
                <TableCell>
                  <TextBlock>
                    Last Name
                  </TextBlock>
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered isDisabled={!!contactData.user} name={`${formPrefix}.lastName`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_PERSON && (
              <TableRow>
                <TableCell>
                  Company
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withSearch
                    withBlankElement
                    isDisabled={contactCompanyList && !contactCompanyList.length}
                    name={`${formPrefix}.companyId`}
                    optionsTotal={contactCompanyList && contactCompanyList.length}
                    onSearch={onContactCompanySearch}
                    onClose={onContactCompanySearchClose}
                  >
                    {menuItemContactCompanyList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
            )
          }
          <TableRow>
            <TableCell>
              <TextBlock>
                Groups:
              </TextBlock>
              <RequiredMark />
            </TableCell>
            <TableCell>
              <FormFieldMultiSelect
                isBordered
                isDisabled={contactGroupList && !contactGroupList.length}
                keyField="name"
                name={`${formPrefix}.groupIds`}
                optionList={contactGroupList}
                onValuesChange={contactGroupsChange}
              >
                {menuItemContactGroupList}
              </FormFieldMultiSelect>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Phone
            </TableCell>
            <TableCell>
              <FieldPhoneBlock>
                <FormFieldPhone
                  isBordered
                  isDisabled={!!contactData.user}
                  name={`${formPrefix}.phone`}
                  onChange={phoneChange}
                />
                <FormFieldNumber
                  isBordered
                  withNoCommas
                  isDisabled={!!contactData.user}
                  name={`${formPrefix}.phoneExtension`}
                  placeholder="Ext"
                />
              </FieldPhoneBlock>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Work Phone
            </TableCell>
            <TableCell>
              <FieldPhoneBlock>
                <FormFieldPhone
                  isBordered
                  isDisabled={!!contactData.user}
                  name={`${formPrefix}.phoneWork`}
                  onChange={phoneWorkChange}
                />
                <FormFieldNumber
                  isBordered
                  withNoCommas
                  isDisabled={!!contactData.user}
                  name={`${formPrefix}.phoneWorkExtension`}
                  placeholder="Ext"
                />
              </FieldPhoneBlock>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <TextBlock>
                Email
              </TextBlock>
              {contactType && contactType === CONTACT_TYPE_PERSON && <RequiredMark />}
            </TableCell>
            <TableCell>
              <FormFieldText isBordered isDisabled={!!contactData.user} name={`${formPrefix}.email`} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              LinkedIn
            </TableCell>
            <TableCell>
              <FormFieldText
                isBordered
                name={`${formPrefix}.linkedin`}
                placeholder={`${URL_LINKEDIN_PROFILE_PREFIX}...`}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Title
            </TableCell>
            <TableCell>
              <FormFieldText isBordered name={`${formPrefix}.title`} />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Address
            </TableCell>
            <TableCell>
              <FieldAddressBlock isCompressed>
                <FormFieldAddress
                  isBordered
                  itemId={contactId}
                  name={`${formPrefix}.streetAddress`}
                  onAddressSearchCompleted={(addressObject: JsonObject): void => {
                    onAddressSearchCompleted(addressObject, contactId);
                  }}
                  onKeyDown={(): void => {
                    onAddressFieldKeyDown(contactId);
                  }}
                />
                <FormFieldText isBordered isDisabled name={`${formPrefix}.location`} />
                <FormFieldText isBordered isDisabled name={`${formPrefix}.postalCode`} />
                <FormFieldText isHidden name={`${formPrefix}.address`} />
              </FieldAddressBlock>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Address Details
            </TableCell>
            <TableCell>
              <FormFieldText
                isBordered
                isMultiline
                name={`${formPrefix}.addressDetails`}
                maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
                minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Description
            </TableCell>
            <TableCell>
              <FormFieldText
                isBordered
                isMultiline
                name={`${formPrefix}.description`}
                maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
              />
            </TableCell>
          </TableRow>
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Industry
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.industry`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Foundation Year
                </TableCell>
                <TableCell>
                  <FormFieldNumber isBordered withNoCommas name={`${formPrefix}.foundationYear`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Headquarter
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.headQuarter`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Website
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.website`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            contactType === CONTACT_TYPE_COMPANY && (
              <TableRow>
                <TableCell>
                  Key Subsidiaries
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.keySubsidiaries`} />
                </TableCell>
              </TableRow>
            )
          }
          <TableRow>
            <TableCell>
              Asset Focus
            </TableCell>
            <TableCell>
              <FormFieldMultiSelect
                isBordered
                isDisabled={assetTypeList && !assetTypeList.length}
                keyField="name"
                name={`${formPrefix}.assetTypes`}
                optionList={assetTypeList}
                onValuesChange={assetTypesChange}
              >
                {menuItemAssetTypeList}
              </FormFieldMultiSelect>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              Tags
            </TableCell>
            <TableCell>
              <FormFieldMultiSelect
                isBordered
                isAbleToAdd
                withSearch
                isDisabled={tagList && !tagList.length}
                keyField="name"
                name={`${formPrefix}.tags`}
                optionList={tagList}
                onSearch={onTagSearch}
                onClose={onTagSearchClose}
                onValuesChange={tagsChange}
              >
                {menuItemTagList}
              </FormFieldMultiSelect>
            </TableCell>
          </TableRow>
          {
            withIsDefaultColumn && (
              <TableRow>
                <TableCell>
                  Is Default
                </TableCell>
                <TableCell>
                  <FormFieldCheckbox name={`${formPrefix}.isDefault`} />
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </Card>
  ) : (
    <TableRowForm isActive={isActive}>
      {
        isActive && withIsDefaultColumn && (
          <TableCellShort isTextCenter>
            <FormFieldCheckbox withNoLabelOffset name={`${formPrefix}.isDefault`} />
          </TableCellShort>
        )
      }
      {
        tableContactListFields && !!tableContactListFields.length
        && StructureService.extendOptions(tableContactListFields, tableColumnSettings, true)
          .map((tableField: TableField, fieldIndex: number): ReactElement | boolean => (
            !!tableField.component && (
              !tableField.isNotFiltered ? (
                <FilteredCell
                  key={tableField.id}
                  tableName={tableName}
                  columnName={tableField.name}
                  currentUser={currentUser}
                >
                  {tableField.component(tableField, fieldIndex)}
                </FilteredCell>
              ) : (
                <Fragment key={tableField.id}>
                  {tableField.component(tableField, fieldIndex)}
                </Fragment>
              )
            )
          ))
      }
    </TableRowForm>
  );
});

export default ContactRowForm;
